import React from 'react';
import { Clock, Users, Wrench, MoreVertical, ActivityIcon, ClipboardList } from 'lucide-react';
import { Button } from "@/modern/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";
import { useTimelineContext } from '../context/TimelineContext';
import { FilterKey, FilterState, EntityConfig } from '../utils/types';

export const TimelineHeader: React.FC = () => {
  const {
    config,
    filters,
    toggleFilter,
    handleCreateReminder,
    handleCreateSchedule,
    handleCreatePlanning
  } = useTimelineContext();

  return (
    <div className="flex-none space-y-3 py-6 px-6">
      {/* Title Row */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-base tracking-tight text-foreground">Timeline</div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Timeline actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {config.createActions.includes('reminder') && (
              <DropdownMenuItem onClick={handleCreateReminder}>
                <Clock className="h-4 w-4 mr-2" />
                Add Reminder
              </DropdownMenuItem>
            )}
            {config.createActions.includes('schedule') && (
              <DropdownMenuItem onClick={handleCreateSchedule}>
                <Users className="h-4 w-4 mr-2" />
                Add Schedule Date
              </DropdownMenuItem>
            )}
            {config.createActions.includes('planning') && (
              <DropdownMenuItem onClick={handleCreatePlanning}>
                <Wrench className="h-4 w-4 mr-2" />
                Add Planning Date
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filter Row */}
      <div className="flex items-center">
        <TimelineFilter filters={filters} toggleFilter={toggleFilter} config={config} />
      </div>
    </div>
  );
};

interface TimelineFilterProps {
  filters: FilterState;
  toggleFilter: (filter: FilterKey) => void;
  config: EntityConfig;
}

const TimelineFilter: React.FC<TimelineFilterProps> = ({ filters, toggleFilter, config }) => {
  return (
    <div className="flex items-center gap-2 w-full">
      <TooltipProvider>
        {config.availableFilters.includes('status') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => toggleFilter("status")}
                className={cn(
                  "w-1/4 shadow-sm hover:bg-emerald-100 dark:hover:bg-emerald-600",
                  filters.status &&
                    "bg-emerald-100 border-emerald-200 text-emerald-800  dark:bg-emerald-600  dark:hover:bg-emerald-600 dark:border-emerald-600 dark:text-emerald-50  dark:hover:text-emerald-50 shadow-sm shadow-emerald-100 dark:shadow-none"
                )}
              >
                <ActivityIcon className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Status</p>
            </TooltipContent>
          </Tooltip>
        )}

        {config.availableFilters.includes('schedule') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => toggleFilter("schedule")}
                className={cn(
                  "w-1/4 shadow-sm hover:bg-sky-100 dark:hover:bg-sky-600",
                  filters.schedule &&
                    "bg-sky-100 border-sky-200 text-sky-800  dark:bg-sky-600  dark:hover:bg-sky-600 dark:border-sky-600 dark:text-sky-50  dark:hover:text-sky-50 shadow-sm shadow-sky-100 dark:shadow-none"
                )}
              >
                <Users className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Scheduled</p>
            </TooltipContent>
          </Tooltip>
        )}

        {config.availableFilters.includes('planning') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => toggleFilter("planning")}
                className={cn(
                  "w-1/4 shadow-sm hover:bg-amber-100 dark:hover:bg-amber-600",
                  filters.planning &&
                    "bg-amber-100 border-amber-200 text-amber-800  dark:bg-amber-600  dark:hover:bg-amber-600 dark:border-amber-600 dark:text-amber-50  dark:hover:text-amber-50 shadow-sm shadow-amber-100 dark:shadow-none"
                )}
              >
                <Wrench className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Planning</p>
            </TooltipContent>
          </Tooltip>
        )}

        {config.availableFilters.includes('history') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => toggleFilter("history")}
                className={cn(
                  "w-1/4 shadow-sm hover:bg-slate-100 dark:hover:bg-slate-600",
                  filters.history &&
                    "bg-slate-100 border-slate-200 text-slate-800  dark:bg-slate-600  dark:hover:bg-slate-600 dark:border-slate-600 dark:text-slate-50  dark:hover:text-slate-50 shadow-sm shadow-slate-100 dark:shadow-none"
                )}
              >
                <ClipboardList className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>History</p>
            </TooltipContent>
          </Tooltip>
        )}

        {config.availableFilters.includes('reminders') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => toggleFilter("reminders")}
                className={cn(
                  "w-1/4 shadow-sm hover:bg-purple-100 dark:hover:bg-purple-600",
                  filters.reminders &&
                    "bg-purple-100 border-purple-200 text-purple-800  dark:bg-purple-600  dark:hover:bg-purple-600 dark:border-purple-600 dark:text-purple-50  dark:hover:text-purple-50 shadow-sm shadow-purple-100 dark:shadow-none"
                )}
              >
                <Clock className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reminders</p>
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </div>
  );
};
